import type { AccountInfo } from "@azure/msal-browser";
import type { ModuleId } from "../auth/config.ts";

/**
 * Minimal shared state. Panels read it and subscribe to changes rather than
 * reaching into each other — the storage panel needs to know whether anyone is
 * signed in, and from step 3 the Graph panel needs both.
 */

export interface AppState {
    account: AccountInfo | null;
    /** Name of the chosen destination directory, once one is connected. */
    folderName: string | null;
    /**
     * The writable directory handle, set only once permission is confirmed
     * granted — never while it merely exists but needs re-granting (§7). This
     * is what export modules write through; they should not re-resolve it from
     * IndexedDB themselves.
     */
    folderHandle: FileSystemDirectoryHandle | null;
    /** Modules the user ticked. Drives which scopes the single consent asks for. */
    selectedModules: ModuleId[];
    /**
     * Modules whose scopes we hold a token for. Determined by probing with a
     * silent token request rather than tracked as a flag — MSAL's cache is the
     * real authority on what was consented, and a local flag would go stale
     * whenever consent is revoked elsewhere.
     */
    grantedModules: ModuleId[];
}

type Listener = (state: Readonly<AppState>) => void;

const state: AppState = {
    account: null,
    folderName: null,
    folderHandle: null,
    selectedModules: [],
    grantedModules: []
};

const listeners = new Set<Listener>();

export function getState(): Readonly<AppState> {
    return state;
}

export function setState(patch: Partial<AppState>): void {
    Object.assign(state, patch);
    for (const listener of listeners) {
        listener(state);
    }
}

export function subscribe(listener: Listener): () => void {
    listeners.add(listener);
    listener(state);
    return () => listeners.delete(listener);
}
